import { Auth } from 'aws-amplify';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useBrandContext } from '~/brands/BrandContext';

import { Link, Text } from '../../../components';
import { useAppContext } from '../../../contexts/AppContext';
import UnauthenticatedLayout from '../../../overwrites/views/layouts/UnauthenticatedLayout/UnauthenticatedLayout';
import { EmailRegex } from '../../../utils/RouteHelper';
import { Form } from '../auth.styles';
import SignupHeader from '../shared/SignupHeader';
import { StyledFooter, StyledTextField, SubmitButton } from './RecoveryMailPage.styles';

export default function RecoveryMail() {
  const { showError } = useAppContext();
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState<string>(undefined);
  const [loading, setLoading] = React.useState(false);
  const { supportEmail } = useBrandContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  function validateForm(): boolean {
    const emailTrimmed = email.trim();
    if (emailTrimmed.length === 0) {
      setError(t('auth.error.email'));
      return false;
    }

    if (!EmailRegex.test(emailTrimmed)) {
      setError(t('auth.error.invalidEmail'));
      return false;
    }

    setError(undefined);
    return true;
  }

  function handleSubmit(event: any) {
    event.preventDefault();
    if (!validateForm() || loading) {
      return;
    }
    void (async () => {
      const sanitizedEmail = email.toLowerCase().trim();
      try {
        setLoading(true);
        await Auth.forgotPassword(sanitizedEmail);
        setLoading(false);
        navigate('/reset-password', { state: { email: sanitizedEmail } });
      } catch (e: any) {
        showError(e.message);
        setLoading(false);
      }
    })();
  }

  const Footer = () => {
    const email = supportEmail || 'support@visitiq.com';
    const href = `mailto:${email}`;
    return (
      <StyledFooter>
        <Text>{t('auth.recovery.footerText')}&nbsp;</Text>
        <Link href={href}>{email}</Link>
      </StyledFooter>
    );
  };

  return (
    <UnauthenticatedLayout title={t('auth.signIn.forgotYourPassword')} header={<SignupHeader />} showBack>
      <Form noValidate onSubmit={handleSubmit}>
        <StyledTextField
          autoComplete="email"
          autoFocus
          fullWidth
          id="email"
          label={t('auth.common.email.label')}
          name="email"
          error={error}
          onChange={(e: any): void => setEmail(e.target.value as string)}
          required
          value={email}
        />
        <SubmitButton type="submit" loading={loading}>
          {t('auth.recovery.resetPasswordButton')}
        </SubmitButton>
        <Footer />
      </Form>
    </UnauthenticatedLayout>
  );
}
